import React, { createContext, FC, useContext, useEffect, useState } from 'react';
import { useAuthState, AuthStates } from '@vis-auth/react';
import { User } from '@vis-auth/vis-user-client';
import { useUserApi } from '../../services';

const UserContext = createContext<User | null>(null);

export const useUser = (): User | null => {
  const user = useContext(UserContext);

  if (!user) {
    throw new Error('useUser must be used inside UserProvider');
  }

  return user;
};

export const UserProvider: FC = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState(false);

  const authState = useAuthState();
  const userApi = useUserApi();

  if (authState.state !== AuthStates.authenticated) {
    throw new Error('UserProvider must be used inside SecureRoute');
  }

  // eslint-disable-next-line camelcase
  const userId = authState.user?.user_id as string;

  useEffect(() => {
    const fetchUser = async (): Promise<void> => {
      try {
        const response = await userApi.getUser({ id: userId });
        setUser(response);
      } catch (e) {
        setError(true);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchUser();
  }, [authState.state, userApi, userId]);

  if (error) {
    throw new Error('Could not fetch user information');
  }

  if (user) {
    return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
  }

  return <div data-testid="user-context-provider">Loading...</div>;
};
